import { WORLD_ROUTES, WorldOrgResponseSchema } from "@uzverse/contracts";
import { ErrorBodySchema, UzError } from "@uzverse/core";
import type { Focus } from "@uzverse/core";
import type { WorldOrgResponse } from "@uzverse/contracts";
import { MOCK_WORLD_ORG } from "./mock-data.js";

function mockMode(): boolean {
  return import.meta.env["VITE_UZ_MOCK"] === "1";
}

/** The signed-in user's companies with buildings, floors, leads and workers. */
export async function fetchWorldOrg(signal?: AbortSignal): Promise<WorldOrgResponse> {
  if (mockMode()) return MOCK_WORLD_ORG;
  const response = await fetch(WORLD_ROUTES.org, {
    credentials: "same-origin",
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) throw new Error(`world: ${response.status}`);
  return WorldOrgResponseSchema.parse(await response.json());
}

/** What a human tool call returns: the output and the id `history.undo` takes to revert it. */
export interface WorldToolResult {
  output: unknown;
  correlationId?: string;
}

type MockInvoker = (name: string, input: unknown, focus: Focus | null) => Promise<WorldToolResult>;
let mockInvoker: MockInvoker | null = null;

/** Mock mode only: the in-browser fake world service answers tool calls (no network). */
export function setMockWorldInvoker(invoker: MockInvoker | null): void {
  mockInvoker = invoker;
}

/**
 * A UI action as a world tool call (`POST /api/v1/world/tools/invoke`): the same pipeline,
 * permissions and audit as the assistant's calls, and no model in between.
 */
export async function invokeWorldTool(
  name: string,
  input: unknown,
  focus: Focus | null = null,
): Promise<WorldToolResult> {
  if (mockMode()) {
    if (!mockInvoker) throw new UzError("unavailable", "the mock world is not running");
    return mockInvoker(name, input, focus);
  }
  let response: Response;
  try {
    response = await fetch(WORLD_ROUTES.toolsInvoke, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name, input, ...(focus ? { focus } : {}) }),
    });
  } catch {
    throw new UzError("unavailable", "network error");
  }
  const json: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const parsed = ErrorBodySchema.safeParse(json);
    if (parsed.success) {
      const { code, message, details } = parsed.data.error;
      throw new UzError(code, message, details);
    }
    throw new UzError("internal", "request failed");
  }
  const body = (json ?? {}) as { output?: unknown; correlationId?: unknown };
  return {
    output: body.output,
    ...(typeof body.correlationId === "string" ? { correlationId: body.correlationId } : {}),
  };
}
